import { FirebaseData } from "../General/FirebaseData";
import { Timestamp, getFirestore, getDocs, collection, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { UnitData } from "../Property/Units/UnitData";
import { PropertyData } from "../Property/PropertyData";
import { LocalData } from "../General/LocalData";
import { SurveyAnswerData } from "./SurveyAnswerData";
import { SurveyQuestionData } from "./SurveyQuestionData";
import * as PopUp from "../../../components/deprecated/Popup/deprecated_PopUps";
import { DataCreator } from "../../../utils/dataCreator";
import { CreatedByData } from "../General/CreatedByData";
import { t } from "i18next";
import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";

export class SurveyData extends FirebaseData {
  name: string = "";
  description: string = "";
  date?: Date;
  allProperties: PropertyData[] = [];
  allUnits: UnitData[] = [];
  properties: { [key: string]: UnitData[] } = {};
  lastReminderDate?: Date;

  isDraft: boolean = false;

  questions: SurveyQuestionData[] = [];

  answers: SurveyAnswerData[] = [];
  loadingAnswers = false;
  loadedAnswers = false;
  loadedAnswersListeners: (() => void)[] = [];

  createdBy?: CreatedByData;

  setup(data: { [key: string]: any }) {
    if (super.isValidString(data.name)) {
      this.name = data.name;
    }
    if (super.isValidString(data.description)) {
      this.description = data.description;
    }
    if (super.isValidBoolean(data.isDraft)) {
      this.isDraft = data.isDraft;
    }

    if (super.isValidObject(data.createdBy)) {
      this.createdBy = new CreatedByData(data.createdBy);
    }
    if (data.lastReminderDate as Timestamp) {
      this.lastReminderDate = (data.lastReminderDate as Timestamp).toDate();
    }
    if (this.isValidDate(data.lastReminderDate)) {
      this.lastReminderDate = data.lastReminderDate;
    }

    if (data.date as Timestamp) {
      this.date = (data.date as Timestamp).toDate();
    }
    if (this.isValidDate(data.date)) {
      this.date = data.date;
    }
    this.properties = {};
    if (this.isValidObject(data.properties)) {
      Object.keys(data.properties).forEach((propertyID) => {
        let units: UnitData[] = [];
        if (Array.isArray(data.properties[propertyID])) {
          data.properties[propertyID].forEach((unitID: string) => {
            units.push(LocalData.properties.addData(propertyID).getUnitWithId(unitID));
          });
        }
        this.properties[propertyID] = units;
      });
    }

    this.allProperties = [];
    if (Array.isArray(data.allProperties)) {
      (data.allProperties as any[]).forEach((propertyID) => {
        if (typeof propertyID === "string") {
          this.allProperties.push(LocalData.properties.addData(propertyID));
        }
      });
    }
    this.allUnits = [];
    if (Array.isArray(data.allUnits)) {
      (data.allUnits as any[]).forEach((unitID) => {
        if (typeof unitID === "string") {
          this.allUnits.push(LocalData.getUnitWithId(unitID));
        }
      });
    }
    this.questions = [];
    if (Array.isArray(data.questions)) {
      (data.questions as { [key: string]: any }[]).forEach((question, index) => {
        const data = new SurveyQuestionData(index + 1);
        data.setup(question);
        this.questions.push(data);
      });
    }
    super.setup(data);
  }

  loadAnswers(loadedListener: () => void) {
    if (!this.loadingAnswers) {
      this.loadingAnswers = true;

      const db = getFirestore();
      const answersCollection = collection(db, "reports", this.id, "answers");

      getDocs(answersCollection)
        .then((snapshot) => {
          this.loadedAnswers = true;
          this.loadingAnswers = false;
          for (let document of snapshot.docs) {
            const answer = this.getAnswerWithId(document.id);
            answer.setup(document.data());
          }
          loadedListener();
        })
        .catch((error) => {
          console.log("Error loading answers");
          console.log(error);
          this.loadedAnswers = true;
          this.loadingAnswers = false;
          loadedListener();
        });
    }
  }

  getAnswerForUnit(unit: UnitData): SurveyAnswerData | undefined {
    for (let answer of this.answers) {
      if (answer.unit === unit) {
        return answer;
      }
    }
    return undefined;
  }

  getAnswerWithId(id: string): SurveyAnswerData {
    for (let answer of this.answers) {
      if (answer.id === id) {
        return answer;
      }
    }
    const answer = new SurveyAnswerData(id, this);
    this.answers.push(answer);
    return answer;
  }

  getDuplicationMap(name: string): { [key: string]: any } {
    const data: { [key: string]: any } = {
      name: name,
      description: this.description,
      isDraft: true,
      questions: this.questions.map((question) => {
        return {
          question: question.question,
          type: question.questionType?.key ?? 0,
          info: question.settings,
        };
      }),
      createdBy: DataCreator.getCreatedBy(),
      allProperties: [],
      allUnits: [],
      properties: {},
    };

    if (this.date) {
      data["date"] = Timestamp.fromDate(this.date);
    }

    return data;
  }

  updateLastReminder(completion: (error?: Error) => void) {
    const db = getFirestore();
    const reportRef = doc(db, "reports", this.id);

    updateDoc(reportRef, {
      lastReminderDate: new Date(),
    })
      .then(() => {
        this.lastReminderDate = new Date();
        completion(undefined);
      })
      .catch((error) => {
        console.log("Error updating lastReminder");
        console.log(error);
        completion(error);
      });
  }

  delete(block: () => void) {
    ToastUtilities.presentInfo(t("main.deletingSurvey"));
    const db = getFirestore();
    const reportRef = doc(db, "reports", this.id);

    deleteDoc(reportRef)
      .then(() => {
        console.log("Duplicated report");
        LocalData.surveys.remove(this);

        ToastUtilities.presentSuccess(t("main.surveyDeleted"));
        block();
      })
      .catch((error) => {
        console.error("There was an error deleting the report: " + error.code + ", message: " + error.message + ", details: " + error.details);
        ToastUtilities.presentError(error.code + ": " + error.message);
      });
  }
}

